import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CredentialsTab = ({ onIssueCredential, onVerifyCredential }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const credentials = [
    {
      id: "CRED-2025-001",
      type: "Vaccination Record",
      title: "COVID-19 Vaccination Certificate",
      issuer: "HealthGuard Pro",
      issuerDID: "did:web:healthguard.pro:issuer",
      issuedDate: "2025-09-20",
      expiryDate: "2026-09-20",
      status: "Active",
      verificationStatus: "Verified",
      blockchainHash: "0x1a2b3c4d5e6f7890abcdef1234567890abcdef12",
      qrCode: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
      details: {
        vaccine: "Pfizer-BioNTech COVID-19",
        doses: "2",
        lastDose: "2025-09-20",
        batchNumber: "FF2345",
        administeredBy: "Dr. Sarah Johnson",
        location: "HealthGuard Clinic"
      },
      documents: ["COVID_Vaccination_Certificate.pdf", "FHIR_Vaccination_Record.json"]
    },
    {
      id: "CRED-2025-002",
      type: "Insurance Coverage",
      title: "Health Insurance Verification",
      issuer: "BlueCross BlueShield",
      issuerDID: "did:web:bcbs.com:issuer",
      issuedDate: "2025-01-01",
      expiryDate: "2025-12-31",
      status: "Active",
      verificationStatus: "Verified",
      blockchainHash: "0x2b3c4d5e6f7890abcdef1234567890abcdef1234",
      qrCode: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
      details: {
        policyNumber: "BCBS-2025-789456",
        groupNumber: "GRP-12345",
        planType: "PPO Premium",
        deductible: "$1,500",
        copay: "$25",
        coverage: "Medical, Dental, Vision"
      },
      documents: ["Insurance_Coverage_Certificate.pdf", "FHIR_Coverage_Record.json"]
    },
    {
      id: "CRED-2025-003",
      type: "Training Certificate",
      title: "CPR Certification",
      issuer: "American Red Cross",
      issuerDID: "did:web:redcross.org:issuer",
      issuedDate: "2025-08-15",
      expiryDate: "2027-08-15",
      status: "Active",
      verificationStatus: "Verified",
      blockchainHash: "0x3c4d5e6f7890abcdef1234567890abcdef123456",
      qrCode: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
      details: {
        certificationLevel: "Adult CPR/AED",
        instructorName: "Jane Smith, RN",
        trainingHours: "8 hours",
        skillsAssessed: "Chest compressions, Rescue breathing, AED usage",
        location: "Red Cross Training Center",
        renewalRequired: "Every 2 years"
      },
      documents: ["CPR_Certificate.pdf", "Training_Record.json"]
    },
    {
      id: "CRED-2025-004",
      type: "Lab Result",
      title: "Blood Type Verification",
      issuer: "HealthLab Central",
      issuerDID: "did:web:healthlab.com:issuer",
      issuedDate: "2025-09-18",
      expiryDate: "N/A",
      status: "Active",
      verificationStatus: "Verified",
      blockchainHash: "0x4d5e6f7890abcdef1234567890abcdef12345678",
      qrCode: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
      details: {
        bloodType: "A+",
        rhFactor: "Positive",
        testMethod: "Forward and Reverse Typing",
        labTechnician: "Michael Johnson, MLT",
        qualityControl: "Passed",
        testAccuracy: "99.9%"
      },
      documents: ["Blood_Type_Certificate.pdf", "FHIR_Lab_Result.json"]
    }
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-success text-success-foreground';
      case 'expired': return 'bg-error text-error-foreground';
      case 'revoked': return 'bg-warning text-warning-foreground';
      case 'pending': return 'bg-secondary text-secondary-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getVerificationColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'verified': return 'text-success';
      case 'unverified': return 'text-warning';
      case 'failed': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'vaccination record': return 'Shield';
      case 'insurance coverage': return 'CreditCard';
      case 'training certificate': return 'GraduationCap';
      case 'lab result': return 'FileText';
      default: return 'Award';
    }
  };

  const filteredCredentials = credentials?.filter(credential => {
    const matchesSearch = credential?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         credential?.issuer?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         credential?.type?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesFilter = filterType === 'all' || credential?.type?.toLowerCase()?.includes(filterType?.toLowerCase());
    
    return matchesSearch && matchesFilter;
  });

  const handleShareCredential = (credential) => {
    // Mock sharing functionality
    console.log('Sharing credential:', credential?.id);
  };

  const handleDownloadQR = (credential) => {
    // Mock QR code download
    console.log('Downloading QR code for:', credential?.id);
  };

  return (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="bg-card border border-border rounded-lg p-4 shadow-clinical">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search credentials by title, issuer, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-4 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Types</option>
              <option value="vaccination">Vaccination Records</option>
              <option value="insurance">Insurance Coverage</option>
              <option value="training">Training Certificates</option>
              <option value="lab">Lab Results</option>
            </select>
            
            <Button variant="outline" iconName="Shield" iconPosition="left" onClick={onVerifyCredential}>
              Verify Credential
            </Button>
            
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={onIssueCredential}>
              Issue Credential
            </Button>
          </div>
        </div>
      </div>
      {/* Credentials List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredCredentials?.map((credential) => (
          <div key={credential?.id} className="bg-card border border-border rounded-lg shadow-clinical">
            {/* Credential Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name={getTypeIcon(credential?.type)} size={20} className="text-accent" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(credential?.status)}`}>
                        {credential?.status}
                      </span>
                      <div className="flex items-center gap-1">
                        <Icon name="CheckCircle" size={14} className={getVerificationColor(credential?.verificationStatus)} />
                        <span className={`text-xs ${getVerificationColor(credential?.verificationStatus)}`}>
                          {credential?.verificationStatus}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">{credential?.title}</h3>
                    <p className="text-sm text-muted-foreground">{credential?.type}</p>
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <Button variant="ghost" size="sm" iconName="QrCode" onClick={() => handleDownloadQR(credential)}>
                    QR
                  </Button>
                </div>
              </div>
            </div>

            {/* Credential Details */}
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">Issuer Information</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Issuer:</span>
                      <span className="text-foreground">{credential?.issuer}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">DID:</span>
                      <span className="text-foreground text-xs font-mono">{credential?.issuerDID}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Issued:</span>
                      <span className="text-foreground">{new Date(credential.issuedDate)?.toLocaleDateString()}</span>
                    </div>
                    {credential?.expiryDate !== 'N/A' && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expires:</span>
                        <span className="text-foreground">{new Date(credential.expiryDate)?.toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-foreground mb-2">Credential Details</h4>
                  <div className="space-y-1 text-sm">
                    {Object.entries(credential?.details)?.map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-muted-foreground capitalize">{key?.replace(/([A-Z])/g, ' $1')}:</span>
                        <span className="text-foreground">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-foreground mb-2">Blockchain Verification</h4>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Link" size={16} className="text-success" />
                      <span className="text-sm font-medium text-success">Anchored on Blockchain</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono break-all">
                      Hash: {credential?.blockchainHash}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-foreground mb-2">Available Documents</h4>
                  <div className="space-y-2">
                    {credential?.documents?.map((document, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                        <div className="flex items-center gap-2">
                          <Icon 
                            name={document?.includes('.pdf') ? 'FileText' : 'Code'} 
                            size={16} 
                            className="text-muted-foreground" 
                          />
                          <span className="text-sm text-foreground">{document}</span>
                        </div>
                        <Button variant="ghost" size="sm" iconName="Download">
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-border">
                  <Button variant="outline" size="sm" iconName="Share" onClick={() => handleShareCredential(credential)}>
                    Share
                  </Button>
                  <Button variant="outline" size="sm" iconName="Eye">
                    View Details
                  </Button>
                  <Button variant="outline" size="sm" iconName="Shield">
                    Verify
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredCredentials?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center shadow-clinical">
          <Icon name="Shield" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No credentials found</h3>
          <p className="text-muted-foreground mb-6">No verifiable credentials match your current search criteria.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" iconName="Shield" iconPosition="left" onClick={onVerifyCredential}>
              Verify Credential
            </Button>
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={onIssueCredential}>
              Issue New Credential
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CredentialsTab;